import { FontVariantStyles } from '@dealstack/frontend/shared/components/Typography/Typography';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { mantineThemeColors } from '@dealstack/frontend/shared/mechanism/styleguide/mantineThemeColors.ts';
import {
	ActionIcon,
	Autocomplete,
	Badge,
	Button,
	Checkbox,
	CloseButton,
	createTheme,
	Divider,
	Drawer,
	Input,
	InputBase,
	Loader,
	Menu,
	Modal,
	MultiSelect,
	NavLink,
	NumberInput,
	Pagination,
	Radio,
	rem,
	SegmentedControl,
	Select,
	Skeleton,
	Switch,
	Tabs,
	Text,
	Textarea,
	TextInput,
} from '@mantine/core';
import { DateInput, DatePicker, DatePickerInput } from '@mantine/dates';

export enum MANTINE_BREAKPOINTS {
	xss = '23em', //368px
	xs = '36em', //576px
	sm = '48em', //768px
	md = '62em', //992px
	lg = '75em', // 1200px
	xl = '89em', // 1424px
	xxl = '100em', //1600px
}

export const MANTINE_QUERIES = {
	min_xss: `(min-width: ${MANTINE_BREAKPOINTS.xss})`,
	min_xs: `(min-width: ${MANTINE_BREAKPOINTS.xs})`,
	min_sm: `(min-width: ${MANTINE_BREAKPOINTS.sm})`,
	min_md: `(min-width: ${MANTINE_BREAKPOINTS.md})`,
	min_lg: `(min-width: ${MANTINE_BREAKPOINTS.lg})`,
	min_xl: `(min-width: ${MANTINE_BREAKPOINTS.xl})`,
	min_xxl: `(min-width: ${MANTINE_BREAKPOINTS.xxl})`,
	max_xss: `(max-width: ${MANTINE_BREAKPOINTS.xss})`,
	max_xs: `(max-width: ${MANTINE_BREAKPOINTS.xs})`,
	max_sm: `(max-width: ${MANTINE_BREAKPOINTS.sm})`,
	max_md: `(max-width: ${MANTINE_BREAKPOINTS.md})`,
	max_lg: `(max-width: ${MANTINE_BREAKPOINTS.lg})`,
	max_xl: `(max-width: ${MANTINE_BREAKPOINTS.xl})`,
	max_xxl: `(max-width: ${MANTINE_BREAKPOINTS.xxl})`,
};

// Custom Text "variants" keyed by FontVariant name (B1Bold, etc.) used as the
// `size` prop. In v6 these were component-level `sizes`; v7 removes that key, so
// they are resolved in the Text `styles` callback below from `props.size`.
const fontVariantSize = (size: string | undefined) => {
	const variant = size
		? Object.entries(FontVariantStyles).find(([key]) => key === size)?.[1]
		: undefined;
	if (!variant) {
		return undefined;
	}
	return {
		fontSize: rem(variant.fontSize),
		lineHeight: rem(variant.lineHeight),
		fontWeight: variant.fontWeight,
	};
};

// Disabled-input fill = gray[4]. v6's disabled look was the base tokens (gray[4]
// fill, gray[8] border, dark[0] text) dimmed by `opacity: 0.6`; the disabled
// blocks below restore that opacity, so this stays the flat base colour and the
// faded appearance is produced by opacity rather than baked into the hex.
const DISABLED_INPUT_BG = '#D3D8DA';

// v7 defaults the calendar weekday header cells (Mo, Tu, ...) to
// `--mantine-color-dimmed` (light gray); v6 rendered them in the body text
// colour. Restore the darker weekday labels so they match the day numbers
// below them. Shared by DatePickerInput, DateInput and the bare DatePicker.
const CALENDAR_WEEKDAY_STYLE = { color: 'var(--mantine-color-text) !important' };

export const v1 = createTheme({
	respectReducedMotion: false,
	defaultRadius: 'sm',
	breakpoints: MANTINE_BREAKPOINTS,
	fontFamily: 'Inter, sans-serif',
	fontFamilyMonospace: 'Overpass Mono',
	shadows: { xs: '2px', sm: '4px', md: '8px', lg: '10px', xl: '12px' },
	radius: { xs: '0px', sm: '4px', md: '8px', lg: '12px', xl: '16px' },
	spacing: { xs: '8px', sm: '16px', md: '24px', lg: '32px', xl: '40px' },
	headings: {
		fontFamily: 'Inter, sans-serif',
		fontWeight: '800',
		sizes: {
			h1: { fontSize: rem('50px'), lineHeight: rem('64px') },
			h2: { fontSize: rem('36px'), lineHeight: rem('48px') },
			h3: { fontSize: rem('30px'), lineHeight: rem('40px') },
			h4: { fontSize: rem('25px'), lineHeight: rem('32px') },
			h5: { fontSize: rem('17px'), lineHeight: rem('24px') },
			h6: { fontSize: rem('14px'), lineHeight: rem('20px') },
		},
	},
	fontSizes: {
		xs: rem('10px'),
		sm: rem('12px'),
		md: rem('14px'),
		lg: rem('17px'),
		xl: rem('25px'),
	},
	colors: mantineThemeColors,
	components: {
		// Replaces the v6 top-level `loader: 'dots'`.
		Loader: Loader.extend({
			defaultProps: { type: 'dots' },
		}),
		// v6 ActionIcon defaulted to a transparent `subtle` variant; v7 defaults to
		// the filled (blue) variant. Restore the v6 default so variant-less icons
		// (e.g. table/header gear, row actions) don't render on a blue background.
		ActionIcon: ActionIcon.extend({
			defaultProps: { variant: 'subtle', color: 'gray' },
			// v7's built-in disabled state hardcodes `background-color:
			// var(--mantine-color-gray-1)` regardless of variant. Our palette's
			// gray[1] (#BDC4C8) is a solid mid-tone rather than a light tint, so a
			// disabled `transparent` ActionIcon (e.g. the SDW counterparty
			// signatory +/- stepper) rendered as a heavy filled box instead of
			// staying invisible like v6. Keep transparent/subtle variants
			// background-free when disabled; the wrapped Icon's own `disabled`
			// prop already handles the faded icon colour.
			styles: (_theme, props) =>
				props.variant === 'transparent' || props.variant === 'subtle'
					? {
							root: {
								':disabled, &[data-disabled]': { backgroundColor: 'transparent' },
							},
						}
					: {},
		}),
		Drawer: Drawer.extend({
			defaultProps: {
				zIndex: Z_INDEX.DRAWER,
			},
			styles: (theme) => ({
				root: {
					'.mantine-Drawer-close': {
						width: '24px',
						height: '24px',
						color: theme.colors.black[0],

						svg: {
							width: '24px !important',
							height: '24px !important',
						},
					},
				},
				content: {
					display: 'flex',
					flexDirection: 'column',
				},
				body: {
					flex: 1,
				},
				title: {
					fontWeight: 600,
					fontSize: rem(25),
				},
			}),
		}),
		Switch: Switch.extend({
			styles: (theme, props) => {
				return {
					// v7 gives the Switch description no explicit line-height; it
					// inherits the InlineInput label-lh, which under our
					// `body { line-height: 1 }` reset resolves to 17.5px instead of
					// v6's 20px (height shrinks 240px -> 210px on a 12-line
					// description). Pin it back to the v6 value.
					description: { lineHeight: rem('20px') },
					...(props.size !== 'sm'
						? {}
						: {
								thumb: {
									width: '1.1rem',
									height: '1.1rem',
									border: 'none',
									left: '0',
								},
								track: {
									height: '1.15rem',
									minWidth: '0.9rem',
									cursor: 'pointer',
								},
								trackLabel: {
									minWidth: '0.9rem',
								},
							}),
					input: {
						'&:checked:not(:disabled) + .mantine-Switch-track': {
							backgroundColor: theme.colors.blue[2],
							border: `0.1rem solid ${theme.colors.blue[6]}`,
							'.mantine-Switch-thumb': {
								backgroundColor: theme.colors.blue[6],
							},
							'.mantine-Switch-trackLabel': {
								backgroundColor: theme.colors.blue[2],
							},
						},
					},
				};
			},
		}),
		Tabs: Tabs.extend({
			// v6 `variants.underline` folded into `styles` (v7 removed `variants`).
			// v7 renames the `tabsList` selector to `list`.
			styles: (theme, props) => ({
				list: { borderBottom: 'none' },
				...(props.variant === 'underline'
					? {
							tab: {
								height: '100%',
								boxSizing: 'border-box',
								padding: '8px',
								fontWeight: 600,
								':hover': { backgroundColor: theme.colors.gray[0] },
								'&[data-active]': {
									backgroundColor: theme.colors.gray[2],
									borderBottom: `2px solid ${theme.colors.dark[8]}`,
								},
							},
						}
					: {}),
			}),
		}),
		Pagination: Pagination.extend({
			styles: (theme) => ({
				control: {
					'&[data-active]': {
						backgroundColor: theme.colors.blue[6],
						color: theme.colors.white[0],
						borderColor: theme.colors.blue[6],
						fontWeight: 600,
						'&:hover': {
							backgroundColor: theme.colors.blue[8],
						},
						'&:active': {
							backgroundColor: theme.colors.blue[9],
						},
					},
				},
			}),
		}),
		Badge: Badge.extend({
			// v6 Badge resolved a default size of `md`; v7 leaves props.size
			// undefined when unset, so the size-keyed `styles` below fell through to
			// no padding (tags rendered cramped). Restore the v6 default.
			defaultProps: { size: 'md' },
			// v7 Badge selectors: root / label / section (v6 `inner` -> `label`,
			// `leftSection`/`rightSection` -> `section`). v6 `sizes` folded in.
			styles: (_theme, props) => {
				const rootBySize: Record<string, Record<string, unknown>> = {
					xs: { padding: '4px 6px 4px 6px', height: '1.25rem', minHeight: '1.25rem' },
					sm: { padding: '4px 8px 4px 6px', height: '1.5rem', minHeight: '1.5rem' },
					md: { padding: '4px 8px 4px 6px', height: '2rem', minHeight: '2rem' },
				};
				const labelBySize: Record<string, Record<string, unknown>> = {
					xs: { fontSize: rem('9px'), lineHeight: '0.75rem' },
					sm: { fontSize: rem('12px'), lineHeight: '0.75rem' },
					md: { fontSize: rem('14px'), lineHeight: '1.125rem' },
				};
				return {
					root: {
						width: 'fit-content',
						...(props.size ? rootBySize[props.size] ?? {} : {}),
					},
					label: {
						textTransform: 'none',
						...(props.size ? labelBySize[props.size] ?? {} : {}),
					},
					section: { lineHeight: 'initial', display: 'flex', alignItems: 'center' },
				};
			},
		}),
		Text: Text.extend({
			// v6 custom `sizes` (FontVariant names) resolved from props.size.
			styles: (_theme, props) => {
				const sizeStyles = fontVariantSize(props.size);
				return sizeStyles ? { root: sizeStyles } : {};
			},
		}),
		Select: Select.extend({
			// v7 portals the Combobox dropdown to <body> at the `popover` z-index
			// (300); our Modal/Drawer are 999, so dropdowns opened inside them render
			// behind. Lift the dropdown above the modal/drawer layer.
			// v7 also flipped `allowDeselect` to default true, so re-clicking the
			// already-selected option fires onChange(null) and wipes required fields
			// (v6 never toggle-deselected). Restore v6 parity; sites that want
			// deselect opt in with explicit allowDeselect.
			defaultProps: {
				comboboxProps: { zIndex: Z_INDEX.GLOBAL_MENU },
				allowDeselect: false,
				// v7 adds a check icon to the selected option by default; v6 showed none
				// (selection is indicated by the `data-checked` row highlight below).
				withCheckIcon: false,
			},
			// v7 Select is Combobox-based: `item` -> `option`; `separator`/
			// `separatorLabel` -> `groupLabel` (TODO: verify grouping visuals).
			styles: (theme, props) => ({
				label: {
					fontWeight: 600,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				dropdown: { boxShadow: '0px 2px 4px 0px #DCDFE0' },
				option: {
					padding: '.25rem',
					fontSize: rem('14px'),
					lineHeight: rem('20px'),
					color: theme.colors.dark[8],
					'&:hover': { backgroundColor: theme.colors.gray[0] },
					// v7 marks the chosen option with `data-checked` (v6 used
					// `data-selected`); without this the selected row lost its blue fill.
					'&[data-checked]': {
						backgroundColor: theme.colors.blue[0],
						border: `1px solid ${theme.colors.blue[2]}`,
						color: theme.colors.dark[8],
						fontWeight: 600,
					},
				},
				groupLabel: {
					fontSize: rem('11px'),
					lineHeight: rem('12px'),
					color: `${theme.colors.gray[9]} !important`,
				},
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { height: '40px', minHeight: '40px' } : {}),

					'&:not(:disabled)': {
						'&:focus, &:hover, &.focus, &.hover': {
							borderColor: theme.colors.dark[0],
						},
					},

					// v7's Combobox-based Select marks the disabled input with
					// `[data-disabled]` (not the native `:disabled`). Pin v6's base tokens
					// (gray[4] fill, gray[8] border, dark[0] text) and keep v6's `opacity:
					// 0.6` dim so the faded v6 look is reproduced, instead of Mantine's
					// default gray-1 fill / gray-6 (near-invisible) text.
					// The `&&` double is required: Mantine's own disabled rule is
					// `.input:disabled, .input[data-disabled]` (specificity 0,2,0); a single
					// `&` ties it and loses on insertion order under the @mantine/emotion
					// bridge, so the default gray-1 leaked. `&&` -> 0,3,0 wins outright.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
						opacity: 0.6,
					},
				},
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
			}),
		}),
		SegmentedControl: SegmentedControl.extend({
			styles: (theme) => ({
				root: {
					backgroundColor: theme.colors.white[0],
					border: `1px solid ${theme.colors.gray[4]}`,
				},
			}),
		}),
		MultiSelect: MultiSelect.extend({
			// Lift the dropdown above the Modal/Drawer layer (see Select above).
			// withCheckIcon: false drops the v7 default check on selected options (v6
			// had none; selection is shown via pills and the data-checked highlight).
			defaultProps: { comboboxProps: { zIndex: Z_INDEX.GLOBAL_MENU }, withCheckIcon: false },
			// v7 MultiSelect is Combobox+Pill based: `item` -> `option`. Pills are
			// styled via the `pill` selector above; the remaining `.mantine-MultiSelect-*`
			// raw selectors below are dead v6 DOM and inert.
			styles: (theme, props) => ({
				label: {
					fontWeight: 600,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				dropdown: { boxShadow: '0px 2px 4px 0px #DCDFE0' },
				pill: {
					backgroundColor: theme.white,
					border: `1px solid ${theme.colors.gray[4]}`,
					color: theme.colors.dark[7],

					'& button': {
						color: theme.colors.gray[9],
					},

					// Mantine only sets `white-space: nowrap` on the Pill root and lets the
					// label inherit it; under the emotion bridge that inheritance is lost,
					// so multi-word labels ("Corporate documents") wrap and the single-line
					// pill height clips them to the first word. Pin nowrap on the label so it
					// stays one line and the pill sizes to the full text (PillGroup wraps).
					'& .mantine-Pill-label': {
						whiteSpace: 'nowrap',
					},
				},
				description: {
					color: theme.colors.dark[8],
				},
				root: {
					...(props.size === 'md'
						? {
								'.mantine-Input-rightSection': {
									marginTop: '0.25rem',
								},
							}
						: {}),
					// v7's MultiSelect search field is `.mantine-MultiSelect-inputField`, not
					// the `.mantine-Input-input` that Select uses, so the base Input
					// placeholder override (gray[9]) never reached it and the empty "Select"
					// placeholder fell back to Mantine's lighter default (gray[5]). Pin
					// gray[9] here to match Select; the root-class prefix lifts specificity
					// above Mantine's own `.m_*::placeholder` rule so it wins under emotion.
					'.mantine-MultiSelect-inputField::placeholder': {
						color: theme.colors.gray[9],
					},
					// Once a value is picked, hide the placeholder behind the pill(s).
					'.mantine-MultiSelect-pillsList:has(.mantine-MultiSelect-pill) .mantine-MultiSelect-inputField::placeholder':
						{
							color: 'transparent',
						},
					'.mantine-MultiSelect-values': {
						height: '100%',
						minHeight: 'unset',
					},

					'.mantine-MultiSelect-value': {
						borderRadius: '1rem',
						height: '1.5rem',
						padding: '0.25rem 0.5rem',
						paddingRight: '0.25rem',
						marginTop: '-1px',
						background: theme.colors.gray[4],
						color: theme.colors.dark[4],

						button: {
							height: rem('20px'),
							width: rem('20px'),
							minHeight: 'unset',
							minWidth: 'unset',
							color: theme.colors.dark[4],
						},
					},
				},
				option: {
					padding: '.25rem',
					fontSize: rem('14px'),
					lineHeight: rem('20px'),
					color: theme.colors.dark[8],
					'&:hover': { backgroundColor: theme.colors.gray[0] },
					// v7 marks the chosen option with `data-checked` (v6 used
					// `data-selected`); without this the selected row lost its blue fill.
					'&[data-checked]': {
						backgroundColor: theme.colors.blue[0],
						border: `1px solid ${theme.colors.blue[2]}`,
						color: theme.colors.dark[8],
						fontWeight: 600,
					},
				},
				input: {
					marginBottom: 0,
					height: 'auto',
					overflow: 'visible',
					...(props.size === 'md' ? { minHeight: '40px' } : {}),

					'&:not(:disabled)': {
						'&:focus, &:focus-within, &:hover, &.focus, &.hover': {
							borderColor: theme.colors.dark[0],
						},
					},

					// v7's Combobox-based MultiSelect marks the disabled input with
					// `[data-disabled]` (not the native `:disabled`). Pin v6's base tokens
					// (gray[4] fill, gray[8] border, dark[0] text) and keep v6's `opacity:
					// 0.6` dim so the faded v6 look is reproduced, instead of Mantine's
					// default gray-1 fill / gray-6 (near-invisible) text.
					// The `&&` double is required: Mantine's own disabled rule is
					// `.input:disabled, .input[data-disabled]` (specificity 0,2,0); a single
					// `&` ties it and loses on insertion order under the @mantine/emotion
					// bridge, so the default gray-1 leaked. `&&` -> 0,3,0 wins outright.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
						opacity: 0.6,
					},
				},
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
			}),
		}),
		TextInput: TextInput.extend({
			styles: (theme, props) => ({
				label: {
					fontSize: rem('11px'),
					fontWeight: 400,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { height: rem('40px'), minHeight: rem('40px') } : {}),
					// `&&` doubles specificity to (0,3,0) so it beats Mantine's own
					// disabled rule `.input:disabled` (0,2,0), which otherwise wins on
					// insertion order under the @mantine/emotion bridge and leaks gray-1.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
					},
				},
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
				description: {
					fontSize: rem('11px'),
					color: `${theme.colors.dark[8]} !important`,
				},
			}),
		}),
		Textarea: Textarea.extend({
			styles: (theme, props) => ({
				label: {
					fontSize: rem('11px'),
					fontWeight: 400,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { minHeight: rem('40px') } : {}),
					// `&&` doubles specificity to (0,3,0) so it beats Mantine's own
					// disabled rule `.input:disabled` (0,2,0), which otherwise wins on
					// insertion order under the @mantine/emotion bridge and leaks gray-1.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
					},
				},
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
				description: {
					fontSize: rem('11px'),
					color: `${theme.colors.dark[8]} !important`,
				},
			}),
		}),
		Autocomplete: Autocomplete.extend({
			// Lift the dropdown above the Modal/Drawer layer (see Select above).
			defaultProps: { comboboxProps: { zIndex: Z_INDEX.GLOBAL_MENU } },
			styles: (_theme, props) => ({
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { height: rem('40px'), minHeight: rem('40px') } : {}),
				},
				...(props.size === 'md'
					? { label: { fontSize: `${rem('14px')} !important` } }
					: {}),
			}),
		}),
		DatePickerInput: DatePickerInput.extend({
			// Lift the calendar popover above the Modal/Drawer layer (see Select above).
			defaultProps: { popoverProps: { zIndex: Z_INDEX.GLOBAL_MENU } },
			styles: (theme, props) => ({
				label: {
					fontSize: rem('11px'),
					fontWeight: 400,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { height: rem('40px'), minHeight: rem('40px') } : {}),
					// `&&` doubles specificity to (0,3,0) so it beats Mantine's own
					// disabled rule `.input:disabled` (0,2,0), which otherwise wins on
					// insertion order under the @mantine/emotion bridge and leaks gray-1.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
					},

					'&:not(:disabled)': {
						'&:focus, &:hover, &.focus, &.hover': {
							borderColor: theme.colors.dark[0],
						},
					},

					'.mantine-DatePickerInput-placeholder': {
						color: theme.colors.gray[9],
					},
				},
				weekday: CALENDAR_WEEKDAY_STYLE,
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
				description: {
					fontSize: rem('11px'),
					color: `${theme.colors.dark[8]} !important`,
				},
			}),
		}),
		NumberInput: NumberInput.extend({
			styles: (theme, props) => ({
				// v7's stepper only draws the left border between the controls and the
				// input; v6 also split the up/down controls with a horizontal divider.
				// Restore it on the up control, matching the input's own border colour.
				control: {
					'&[data-direction="up"]': {
						borderBottom: '1px solid var(--input-bd)',
					},
				},
				label: {
					fontSize: rem('11px'),
					fontWeight: 400,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { height: rem('40px'), minHeight: rem('40px') } : {}),
					// `&&` doubles specificity to (0,3,0) so it beats Mantine's own
					// disabled rule `.input:disabled` (0,2,0), which otherwise wins on
					// insertion order under the @mantine/emotion bridge and leaks gray-1.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
					},

					'&:read-only': {
						borderColor: 'transparent !important',
						cursor: 'auto',
					},
				},
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
				description: {
					fontSize: rem('11px'),
					color: `${theme.colors.dark[8]} !important`,
				},
			}),
		}),
		DateInput: DateInput.extend({
			// Lift the calendar popover above the Modal/Drawer layer (see Select above).
			defaultProps: { popoverProps: { zIndex: Z_INDEX.GLOBAL_MENU } },
			styles: (theme, props) => ({
				label: {
					fontSize: rem('11px'),
					fontWeight: 400,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				input: {
					marginBottom: 0,
					...(props.size === 'md' ? { height: rem('40px'), minHeight: rem('40px') } : {}),
					// `&&` doubles specificity to (0,3,0) so it beats Mantine's own
					// disabled rule `.input:disabled` (0,2,0), which otherwise wins on
					// insertion order under the @mantine/emotion bridge and leaks gray-1.
					'&&:disabled, &&[data-disabled]': {
						border: `solid 1px ${theme.colors.gray[8]}`,
						backgroundColor: DISABLED_INPUT_BG,
						color: theme.colors.dark[0],
					},
				},
				weekday: CALENDAR_WEEKDAY_STYLE,
				error: {
					fontSize: rem('12px'),
					color: `${theme.colors.red[6]} !important`,
				},
			}),
		}),
		DatePicker: DatePicker.extend({
			styles: () => ({
				weekday: CALENDAR_WEEKDAY_STYLE,
			}),
		}),
		Checkbox: Checkbox.extend({
			styles: (theme) => ({
				root: {
					display: 'flex',
					alignItems: 'center',
				},
				input: {
					cursor: 'pointer',
					'&:not(:checked)': {
						border: `2px solid ${theme.colors.dark[9]}`,
					},
					'&:checked:disabled': {
						opacity: '32%',
						backgroundColor: theme.colors.blue[6],
						border: 'none',
					},
					'&:disabled': {
						border: `1px solid ${theme.colors.gray[8]}`,
						backgroundColor: theme.colors.white[0],
						opacity: '32%',
						cursor: 'not-allowed',
						pointerEvents: 'all',
					},
				},
				label: {
					fontSize: rem('14px'),
					// v7 sets --label-lh to the checkbox size (0.875rem = 14px), so
					// the label line-height collapses to 14px and wrapped labels lose
					// height (42px -> 28px on a 2-line label). Restore the v6 21px.
					lineHeight: rem('21px'),
					paddingLeft: '0.5rem',
				},
				error: {
					fontSize: rem('14px'),
					lineHeight: rem('20px'),
					paddingLeft: '0.5rem',
					color: `${theme.colors.red[6]} !important`,
				},
			}),
		}),
		Radio: Radio.extend({
			styles: {
				label: {
					fontSize: rem('14px'),
					paddingLeft: '0.5rem',
				},
			},
		}),
		Divider: Divider.extend({
			defaultProps: {
				color: 'gray.4',
			},
			styles: () => ({
				root: {
					width: '100%',
				},
			}),
		}),
		Button: Button.extend({
			defaultProps: {
				size: 'md',
			},
			// v6 `sizes` + `variants` folded into a single `styles` callback (v7
			// removed both keys). v7 Button selectors: root / inner / section /
			// label / loader. v6 `icon`/`leftIcon`/`rightIcon` -> `section`.
			styles: (theme, props) => {
				const size = props.size;
				// v6 resolved the default variant to `filled` and passed it into the
				// variant callback, so variant-less buttons (e.g. Update deal) picked up
				// the filled styles. v7 leaves props.variant undefined when unset, so
				// restore the v6 default here.
				const variant = props.variant ?? 'filled';
				const sizeRootBySize: Record<string, Record<string, unknown>> = {
					xs: { padding: '4px', fontSize: rem('9px'), lineHeight: rem('12px') },
					sm: { padding: '8px', fontSize: rem('11px'), lineHeight: rem('16px') },
					md: { padding: '8px', fontSize: rem('14px'), lineHeight: rem('20px') },
					lg: { padding: '8px', fontSize: rem('17px'), lineHeight: rem('24px') },
					// Non-standard alias used only by the table "Clear all" link
					// (AppliedFiltersSubrow). v7 emits `var(--mantine-font-size-small)` /
					// `var(--button-padding-x-small)` (both undefined), so the font would
					// collapse to the 16px body base and padding-inline would fall back to
					// the 18px `sm` value. Pin an explicit 13px + 0 padding (the other sizes
					// set their own padding, so this must too or it inherits the 18px
					// fallback).
					small: { padding: 0, fontSize: rem('13px'), lineHeight: rem('20px') },
					// Fixed-height sizes from the design system's Button XS / S / M (Dealstack AI screens).
					'height-28': {
						height: rem('28px'),
						padding: '0 8px',
						fontSize: rem('14px'),
						lineHeight: rem('20px'),
					},
					'height-36': {
						height: rem('36px'),
						padding: '0 8px',
						fontSize: rem('14px'),
						lineHeight: rem('20px'),
					},
					'height-40': {
						height: rem('40px'),
						padding: '0 8px',
						fontSize: rem('14px'),
						lineHeight: rem('20px'),
					},
					// `root.height` below is unconditionally `'auto'`, so these `compact-*`
					// sizes (e.g. the bank-details "Try again" button) had no entry here to
					// override it and fell back to bare line-height with zero vertical
					// padding, collapsing to ~14px tall. Reuse each size's own fontSize/
					// lineHeight and halve its padding, which reproduces Mantine's own
					// compact-* height scale (22/26/30/34) exactly.
					'compact-xs': { padding: '2px', fontSize: rem('9px'), lineHeight: rem('12px') },
					'compact-sm': {
						padding: '4px',
						fontSize: rem('11px'),
						lineHeight: rem('16px'),
					},
					'compact-md': {
						padding: '4px',
						fontSize: rem('14px'),
						lineHeight: rem('20px'),
					},
					'compact-lg': {
						padding: '4px',
						fontSize: rem('17px'),
						lineHeight: rem('24px'),
					},
				};
				const sizeRoot = size ? sizeRootBySize[size] ?? {} : {};

				type VariantStyle = {
					root?: Record<string, unknown>;
					section?: Record<string, unknown>;
				};
				const variantStyles: Record<string, VariantStyle> = {
					filled: {
						root: {
							fontWeight: 400,
							// v7 derives the filled hover from primaryShade+1 (blue[7]),
							// which is empty in our sparse palette -> hover bg vanishes.
							// Pin explicit hover/active shades like the `primary` variant.
							':hover, &.hover': { backgroundColor: theme.colors.blue[8] },
							':active, &.active': { backgroundColor: theme.colors.blue[9] },
							// v7's built-in filled disabled bg/text is darker than v6's.
							// Restore v6's built-in disabled look (gray[2] bg, gray[5] text)
							// so disabled buttons (e.g. Update deal) stay soft and faint.
							':disabled, &[data-disabled]': {
								backgroundColor: theme.colors.gray[2],
								color: theme.colors.gray[5],
							},
						},
					},
					subtle: {
						root: {
							':disabled': {
								backgroundColor: theme.colors.gray[0],
								color: theme.colors.gray[8],
							},
						},
					},
					// v7's raw disabled style fills the bg with gray-1 regardless of
					// variant, so a disabled `transparent` text button (e.g. the bank
					// form's "Check account") renders as a solid gray block. Keep the
					// background transparent and only dim the label, like a disabled link.
					transparent: {
						root: {
							':disabled, &[data-disabled]': {
								cursor: 'not-allowed',
								backgroundColor: 'transparent',
								color: theme.colors.gray[5],
							},
						},
					},
					// v7 has no DealStack `outline` variant, so disabled outline buttons
					// (e.g. the bank-details Reset) fell through to Mantine's raw default
					// (gray-2 fill, gray-5 text, no border) — the "Reset" label rendered as
					// near-invisible gray-5 on gray-2. Match the disabled Input look (gray[4]
					// fill, gray[8] border, dark[0] text, opacity 0.6) so disabled outline
					// buttons read as a muted, bordered control like every other disabled field.
					outline: {
						root: {
							':disabled, &[data-disabled]': {
								cursor: 'not-allowed',
								border: `solid 1px ${theme.colors.gray[8]}`,
								backgroundColor: DISABLED_INPUT_BG,
								color: theme.colors.dark[0],
								opacity: 0.6,
							},
						},
					},
					default: {
						root: {
							fontWeight: 400,
							// v7's built-in `default` variant text is a light grey; v6 used
							// dark body text. Pin it so labels (e.g. Invite a team member)
							// stay legible.
							color: theme.colors.dark[8],
							// v7's built-in default hover-color is also a light grey, making
							// hovered buttons (+ their currentColor icons) look disabled. Hold
							// the dark body colour on hover.
							':hover, &.hover': {
								color: theme.colors.dark[8],
							},
							// v7's built-in disabled bg for the `default` variant is gray-1
							// (darker than v6's gray-2), making disabled `default` buttons (e.g.
							// the valuation FX converter) read as a heavy filled block. Pin the
							// soft v6 disabled look.
							':disabled, &[data-disabled]': {
								backgroundColor: theme.colors.gray[2],
								color: theme.colors.gray[9],
							},
						},
					},
					primary: {
						root: {
							backgroundColor: theme.colors.blue[6],
							color: theme.colors.white[0],
							':hover, &.hover': { backgroundColor: theme.colors.blue[8] },
							':active, &.active': { backgroundColor: theme.colors.blue[9] },
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.white[0],
								backgroundColor: theme.colors.blue[6],
							},
						},
						section: { color: theme.colors.white[0] },
					},
					'primary-dark': {
						root: {
							backgroundColor: theme.colors.blue[4],
							color: theme.colors.black[0],
							':hover, &.hover': { backgroundColor: theme.colors.blue[2] },
							':active, &.active': { backgroundColor: theme.colors.blue[0] },
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.black[0],
								backgroundColor: theme.colors.blue[4],
							},
						},
						section: { color: theme.colors.black[0] },
					},
					secondary: {
						root: {
							backgroundColor: theme.colors.gray[4],
							color: theme.colors.dark[8],
							':hover, &.hover': { backgroundColor: theme.colors.gray[8] },
							':active, &.active': {
								backgroundColor: theme.colors.dark[8],
								color: theme.colors.white[0],
								svg: { color: theme.colors.white[0] },
							},
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.dark[8],
								backgroundColor: theme.colors.gray[4],
							},
						},
						section: { color: theme.colors.dark[8] },
					},
					'secondary-dark': {
						root: {
							backgroundColor: theme.colors.dark[6],
							color: theme.colors.gray[2],
							':hover, &.hover': { backgroundColor: theme.colors.dark[2] },
							':active, &.active': {
								backgroundColor: theme.colors.gray[2],
								color: theme.colors.black[0],
								svg: { color: theme.colors.black[0] },
							},
							':disabled': {
								cursor: 'not-allowed',
								opacity: '64%',
								color: theme.colors.gray[2],
								backgroundColor: theme.colors.dark[6],
								svg: { color: theme.colors.gray[2] },
							},
						},
						section: { color: theme.colors.gray[2] },
					},
					tertiary: {
						root: {
							backgroundColor: theme.colors.white[0],
							border: `1px solid ${theme.colors.blue[6]}`,
							color: theme.colors.blue[6],
							':hover, &.hover': { backgroundColor: theme.colors.gray[2] },
							':active, &.active': { backgroundColor: theme.colors.gray[6] },
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.blue[6],
								backgroundColor: theme.colors.white[0],
							},
						},
						section: { color: theme.colors.blue[6] },
					},
					deletion: {
						root: {
							backgroundColor: theme.colors.red[6],
							color: theme.colors.white[0],
							':hover, &.hover': { backgroundColor: theme.colors.red[8] },
							':active, &.active': { backgroundColor: theme.colors.red[9] },
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.white[0],
								backgroundColor: theme.colors.red[6],
							},
						},
						section: { color: theme.colors.white[0] },
					},
					ghost: {
						root: {
							backgroundColor: 'transparent',
							color: theme.colors.blue[6],
							// v7's built-in hover rule sets `color: var(--button-hover-color,
							// var(--button-color))`; we never define either var for this custom
							// variant, so it falls back to Mantine's white default and the blue
							// label washes out on hover even though the resting-state override
							// above wins. Re-pin blue text alongside the hover background.
							':hover, &.hover': {
								backgroundColor: theme.colors.blue[0],
								color: theme.colors.blue[6],
							},
							':active, &.active': {
								backgroundColor: theme.colors.blue[4],
								color: theme.colors.black[0],
								svg: { color: theme.colors.black[0] },
							},
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.blue[6],
								backgroundColor: 'transparent',
							},
						},
						section: { color: theme.colors.blue[6] },
					},
					'link-color': {
						root: {
							backgroundColor: 'transparent',
							borderRadius: 0,
							color: theme.colors.blue[6],
							borderBottom: `1px solid transparent`,
							// v7's base Button hover fills the bg with `--button-hover`
							// (blue) and swaps text to `--button-color` (white); the v6
							// link-color hover was only a blue underline. Re-pin the
							// transparent bg + blue text so the layered base hover doesn't
							// turn this into a filled blue button.
							':hover, &.hover': {
								backgroundColor: 'transparent',
								color: theme.colors.blue[6],
								borderBottom: `1px solid ${theme.colors.blue[6]}`,
							},
							':active, &.active': {
								backgroundColor: 'transparent',
								color: theme.colors.blue[9],
								svg: { color: theme.colors.blue[9] },
							},
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.blue[6],
								backgroundColor: 'transparent',
							},
						},
						section: { color: theme.colors.blue[6] },
					},
					'link-grey': {
						root: {
							backgroundColor: 'transparent',
							borderRadius: 0,
							color: theme.colors.dark[8],
							// v7's base Button hover fills the bg with `--button-hover`
							// (blue) and swaps text to `--button-color` (white); the v6
							// link-grey hover only recolored the text/icon. Re-pin the
							// transparent bg so the layered base hover doesn't turn this
							// into a filled blue button.
							':hover, &.hover': {
								backgroundColor: 'transparent',
								color: theme.colors.blue[6],
								svg: { color: theme.colors.blue[6] },
							},
							':active, &.active': {
								backgroundColor: 'transparent',
								color: theme.colors.blue[9],
								svg: { color: theme.colors.blue[9] },
							},
							':disabled': {
								cursor: 'not-allowed',
								opacity: '32%',
								color: theme.colors.dark[8],
								backgroundColor: 'transparent',
							},
						},
						section: { color: theme.colors.dark[8] },
					},
				};

				const variantStyle = variant ? variantStyles[variant] : undefined;

				return {
					root: {
						height: 'auto',
						// v7's base Button root sets `overflow: hidden`, and per CSS Flexbox
						// §4.5 the automatic minimum size (`min-width: auto`) only resolves to
						// the content size when the main-axis overflow is `visible` — with
						// `hidden` it resolves to 0. So any Button sitting in a tight flex row
						// shrinks below its own label, and because the label is
						// `white-space: nowrap` the text is hard-clipped mid-glyph rather than
						// wrapping ("Choose file⌐", "Try agai"). Restore the content-based
						// floor explicitly. `max-content` rather than `flex-shrink: 0` so
						// `fullWidth` buttons can still shrink from 100% down to their label
						// width instead of forcing the row to overflow.
						minWidth: 'max-content',
						...sizeRoot,
						...(variantStyle?.root ?? {}),
					},
					inner: {
						gap: '4px',
					},
					section: {
						marginRight: '0px !important',
						marginLeft: '0px !important',
						...(variantStyle?.section ?? {}),
					},
				};
			},
		}),
		Input: Input.extend({
			styles: (theme, props) => ({
				root: {
					marginTop: '12px',
					marginBottom: '12px',
				},
				wrapper: {
					// Hide the down-arrow on disabled Select-family fields: the arrow implies
					// an interactivity the field no longer has. Targets the whole right
					// section (`data-position="right"`) so it works whether the arrow is
					// Mantine's built-in Combobox chevron OR a custom `rightSection` caret
					// icon (e.g. the valuation Tag(s) MultiSelect passes its own caret-down).
					// The wrapper gets `data-disabled` from Mantine's `mod: { disabled }`; the
					// `:is(...)` scope keeps disabled TextInput/NumberInput right sections
					// (currency/unit suffixes) visible — only selects lose their arrow.
					'&[data-disabled]:is(.mantine-Select-wrapper, .mantine-MultiSelect-wrapper, .mantine-NativeSelect-wrapper) [data-position="right"]':
						{
							display: 'none',
						},
				},
				input: {
					marginTop: '4px',
					// v6's native emotion let each concrete component's `input:
					// { marginBottom: 0 }` win over this base value, so inputs carried no
					// bottom margin. Under the v7 @mantine/emotion bridge the base Input
					// rule wins instead, leaking ~12px below every field (e.g. the extra gap
					// above the valuation Tag(s) row). Default to 0 to restore v6 spacing.
					marginBottom: 0,
					lineHeight: rem('20px'),
					color: theme.colors.dark[9],
					backgroundColor: theme.white,
					paddingTop: rem('8px'),
					paddingBottom: rem('8px'),

					// Skip the compact padding on any edge with a section, else the hard
					// paddingLeft/Right clobbers Mantine's section-width reservation.
					...(props.leftSection ? {} : { paddingLeft: rem('8px') }),

					'&:not(.mantine-MultiSelect-input)': {
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					},

					...(props.rightSection
						? {}
						: {
								'&:not(.mantine-Select-input):not(.mantine-MultiSelect-input)': {
									paddingRight: rem('8px'),
								},
							}),

					'&.mantine-MultiSelect-input': {
						paddingLeft: '0',
					},

					'&.mantine-MultiSelect-input .mantine-MultiSelect-pillsList': {
						paddingLeft: rem('8px'),
					},

					'&.mantine-MultiSelect-input .mantine-MultiSelect-values .mantine-MultiSelect-searchInput':
						{
							width: '100%',
							height: '100%',
							paddingTop: rem('8px'),
							paddingBottom: rem('8px'),
							textOverflow: 'ellipsis',

							'&::placeholder': {
								color: theme.colors.gray[9],
							},
						},

					'&.mantine-MultiSelect-input .mantine-MultiSelect-values .mantine-MultiSelect-value':
						{
							backgroundColor: theme.colors.gray[2],
						},

					'&.mantine-Input-input': {
						'&::placeholder': {
							color: theme.colors.gray[9],
						},

						// v6 dimmed disabled inputs with `opacity: 0.6` over the base tokens
						// (gray[4] fill, gray[8] border, dark[0] text); the disabled blocks
						// pin those tokens, so keep 0.6 here to reproduce the faded v6 look.
						// `:has(input:disabled)` covers composite inputs (MultiSelect) whose
						// disabled field is nested.
						'&:disabled, &:has(input:disabled)': {
							opacity: 0.6,
						},

						'&:not(:read-only):not(:disabled)': {
							'&:focus, &:hover, &.focus, &.hover': {
								borderColor: theme.colors.dark[0],
							},
						},

						'&:read-only:not([type]="select")': {
							borderColor: 'transparent !important',
							cursor: 'auto',
						},

						// v7 flags the invalid state with `data-error` on the input
						// (v6 used `data-invalid`/`.invalid`, both dead now), so the red
						// background was never applied.
						'&[data-error]': {
							backgroundColor: theme.colors.red[2],
							'&.mantine-Input-input': {
								color: 'initial',
								'&::placeholder': {
									color: theme.colors.dark[9],
								},
							},
						},
					},
				},
				section: {
					color: theme.colors.black[0],
				},
			}),
		}),
		// CreatableSelect's label renders via InputBase; without this it falls through
		// to the InputWrapper size-map (no `md` key -> 11px). Mirror the Select label
		// rule so it matches sibling Select/TextInput labels at `size="md"` (14px).
		InputBase: InputBase.extend({
			styles: (theme, props) => ({
				label: {
					fontWeight: 600,
					color: `${theme.colors.dark[8]} !important`,
					...(props.size === 'md' ? { fontSize: `${rem('14px')} !important` } : {}),
				},
				// InputBase's input is `.mantine-InputBase-input`, not the
				// `.mantine-Input-input` the base Input placeholder override targets, so
				// the gray[9] placeholder never reached CreatableSelect and it fell back
				// to Mantine's lighter default (see the MultiSelect note above). Pin
				// gray[9] here — the root-class prefix lifts specificity above Mantine's
				// own `.m_*::placeholder` — so it matches sibling Select/TextInput fields.
				root: {
					'.mantine-InputBase-input::placeholder': {
						color: theme.colors.gray[9],
					},
				},
			}),
		}),
		InputWrapper: Input.Wrapper.extend({
			styles: (theme, props) => ({
				root: {
					borderBottom: 'none !important',
				},
				label: {
					color: theme.colors.dark[8],
					'&.mantine-InputWrapper-label': {
						fontWeight: 600,
						fontSize: props.size
							? {
									xl: '18px',
									lg: '14px',
									xs: '9px',
								}[props.size] ?? '11px'
							: '11px',
					},
				},
			}),
		}),
		Modal: Modal.extend({
			styles: (theme) => ({
				body: {
					padding: '2rem',
				},
				header: {
					padding: '2rem 2rem 1rem 2rem',
				},
				title: {
					color: theme.colors.dark[8],
					fontWeight: 600,
					fontSize: rem(25),
				},
			}),
			defaultProps: {
				zIndex: Z_INDEX.MODAL,
				closeButtonProps: {
					size: '2.2rem',
				},
			},
		}),
		CloseButton: CloseButton.extend({
			defaultProps: {
				variant: 'transparent',
			},
		}),
		Menu: Menu.extend({
			styles: (theme) => ({
				item: {
					padding: 0,
					'&[data-hovered]': {
						backgroundColor: theme.colors.gray[0],
					},
				},
			}),
		}),
		NavLink: NavLink.extend({
			defaultProps: {
				bg: 'gray.0',
				mb: '8px',
				ml: '32px',
				py: '16px',
				fw: '600',
			},
			styles: (theme) => ({
				root: {
					'&:hover': {
						backgroundColor: theme.colors.gray[1],
					},
				},
			}),
		}),
		Skeleton: Skeleton.extend({
			defaultProps: {
				visible: true,
				animate: true,
			},
			// v7 Skeleton has only a `root` selector; the v6 `visible` selector
			// styles are applied via the `[data-visible]` attribute on root.
			styles: () => ({
				root: {
					position: 'relative',
					WebkitTransform: 'translateZ(0)',

					'&[data-visible]': {
						overflow: 'hidden',

						'&::before': {
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							content: '""',
							// Theme doesn't seem to be working here, so we need to use a hardcoded color
							backgroundColor: '#fff',
							zIndex: 10,
						},

						'&::after': {
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							content: '""',
							// Theme doesn't seem to be working here, so we need to use a hardcoded color
							backgroundColor: '#E9EBED',
							animation: 'fade 1500ms linear infinite',
							zIndex: 11,
						},
					},
				},
			}),
		}),
	},
});

export const mantineTheme = {
	v1,
};
